import React, { useEffect } from 'react';
import { v4 as uuid } from 'uuid';
import _uniqBy from 'lodash-es/uniqBy';
import { Tag } from '@teetsh/app/src/types/event';
import {
  bgColors,
  getTextColorFromBackgroundColor,
} from '@teetsh/app/src/components/colors';
import tw, { css } from 'twin.macro';
import useClassrooms from '@teetsh/app/src/containers/App/context/ClassroomsContext/useClassrooms';
import { Classroom } from '@teetsh/app/src/types/classroom';
import Popover from 'components/Popover';
import { PlusIcon as Plus } from '@heroicons/react/24/outline';
import { TagList } from './TagList';
import { TagForm } from 'components/Form/TagPicker/TagForm';
import useCreateTag from 'queries/tags/useCreateTag';
import { useSchoolSchoolyear } from 'containers/App/context/SchoolyearSchoolProvider';
import { listDefaultTags } from '@teetsh/app/src/containers/MesAnneesScolaires/School/Tags/listDefaultTags';
import { useTranslation } from 'react-i18next';
import { ColorPaletteConfig } from 'containers/App/context/useColorPalette';

export interface Props {
  value?: string;
  onChange: (v?: Tag) => void;
  smallHeight?: boolean;
  smallerHeight?: boolean;
  classroomIds?: string[];
  disableEdit?: boolean;
  onAddTagToClassroom: (p: {
    tagId: string;
    classroomId: string;
  }) => Promise<any>;
  colorPalette: ColorPaletteConfig;
}

function getOptions(classrooms: Classroom[]): Tag[] {
  return _uniqBy(
    classrooms.reduce((acc, num) => [...acc, ...num.tags], [] as Tag[]),
    t => t.id,
  );
}

export function TagPicker(props: Props) {
  const {
    value,
    onChange,
    smallHeight,
    smallerHeight,
    onAddTagToClassroom,
    classroomIds,
    disableEdit,
    colorPalette,
  } = props;

  const classrooms = useClassrooms();
  const { t } = useTranslation(['common']);
  const currentClassrooms = (classroomIds || [])
    .map(clId => classrooms.find(c => c.id === clId))
    .filter(c => c) as Classroom[];

  const currentClassroomsOptions = getOptions(currentClassrooms);
  const allClassroomsOptions = getOptions(classrooms);

  const { schoolyear, school } = useSchoolSchoolyear();
  const createTag = useCreateTag({
    schoolyearId: schoolyear.id,
    schoolId: school.id,
  });
  const [isFormNewTagOpen, setIsFormNewTagOpen] = React.useState(false);
  const [isOpen, setIsOpen] = React.useState(false);
  const [currentOpenTag, setCurrentOpenTag] = React.useState<
    string | undefined
  >(undefined);
  const currentTag = allClassroomsOptions.find(tt => tt.id === value);
  useEffect(() => {
    // Si le tag est changé depuis l'extérieur, alors on le met à jour
    onChange(currentTag);
  }, [JSON.stringify(currentTag), onChange]);

  // Faut qu'on arrête d'utiliser notre composant bouton, on est obligé de le surcharger dans tous le sens.
  // Quand l'ui refresh passera dessus, on utilisera un composant à part

  return (
    <Popover
      open={isOpen}
      portal
      fill
      placement="bottom-end"
      onClickOutside={() => {
        if (!currentOpenTag) {
          setIsOpen(false);
        }
      }}
    >
      <div>
        <Popover.Button
          onClick={() => {
            setIsOpen(old => !old);
          }}
          css={[
            currentTag && getTextColorFromBackgroundColor(currentTag.color),
            currentTag && bgColors(currentTag.color),
            tw`whitespace-nowrap text-sm font-medium rounded-md border-none`,
            css`
              // c'est de la grosse merde (cf commentaire plus haut)

              &:focus {
                ${currentTag?.color && bgColors(currentTag?.color)}
              }

              &:hover {
                ${currentTag?.color && bgColors(currentTag?.color)}
                filter: ${currentTag ? 'brightness(0.90)' : 'none'};
              }
            `,
            currentTag &&
              (currentTag.color === 'white' ||
                currentTag.color === 'gris-supershy') &&
              tw`ring-1 ring-inset ring-neutral-quiet`,
            !currentTag &&
              tw`border-dashed border-2 hover:border-neutral-intense text-neutral-quiet hover:text-neutral-moderate`,
          ]}
          aria-label="bouton etiquette"
          intent="empty"
          small
          fill
        >
          <span tw="truncate">
            {currentTag ? currentTag.title : 'Ajouter une étiquette'}
          </span>
        </Popover.Button>
      </div>
      <Popover.Panel size="2xl">
        <div
          data-testid="tag-picker"
          css={[
            tw`flex flex-col gap-y-1 px-2 pb-4 overflow-y-auto`,
            // Dans le cas du disable edit comme il n'y a pas les boutons pour mettre
            // du spacing entre les éléments, on doit ajouter des mb-1.5 donc ici on ajoute
            // un pour éviter que ça fasse trop moche
            disableEdit && tw`pt-2`,
            smallerHeight &&
              css`
                max-height: 40vh;
              `,
            smallHeight &&
              css`
                max-height: 50vh;
              `,
            !smallHeight &&
              !smallerHeight &&
              css`
                max-height: 50vh;
              `,
          ]}
        >
          {!disableEdit && (
            <div tw="flex justify-between items-center pt-2">
              <div tw="font-semibold pl-3 pt-1 text-muted">
                {t('common:components.tagPicker.tags')}
              </div>
              {!disableEdit && (
                <Popover
                  portal
                  fill
                  placement="bottom-end"
                  open={isFormNewTagOpen}
                >
                  <Popover.Button
                    onClick={() => setIsFormNewTagOpen(true)}
                    minimal
                    small
                    icon={Plus}
                  />
                  <Popover.Panel size="xl">
                    <div tw="p-4">
                      <TagForm
                        onClose={() => setIsFormNewTagOpen(false)}
                        options={listDefaultTags(
                          currentClassrooms.flatMap(c => c.cycle),
                        )}
                        onSubmit={tt =>
                          createTag({
                            ...tt,
                            id: uuid(),
                            classrooms: currentClassrooms.map(cl => ({
                              id: cl.id,
                            })),
                          })
                        }
                        colorPalette={colorPalette}
                      />
                    </div>
                  </Popover.Panel>
                </Popover>
              )}
            </div>
          )}
          <TagList
            disableEdit={disableEdit}
            setCurrentTag={setCurrentOpenTag}
            onChange={tt => {
              onChange(tt);
              setIsOpen(false);
            }}
            currentClassrooms={currentClassrooms}
            currentTagOpen={currentOpenTag}
            onAddTagToClassroom={onAddTagToClassroom}
            options={currentClassroomsOptions}
            colorPalette={colorPalette}
          />
        </div>
      </Popover.Panel>
    </Popover>
  );
}
